<template>
  <div>
    <v-card flat>
      <v-card-title class="d-flex align-center justify-end">
        <f-btn edit v-if="user_allowed" small class="ml-2" @click="editDialog = true"></f-btn>
      </v-card-title>
      <v-card-text>
        <v-dialog v-model="editDialog" max-width="800px" persistent>
          <v-card>
            <v-card-title class="red white--text">
              <span class="headline">Formas de Pagamento e Prazos Permitidos</span>
            </v-card-title>
            <v-card-text>
              <v-alert type="info" class="my-2">
                <div class="text-body-2">
                  <strong>Instruções:</strong><br>
                  • Se nenhuma opção for selecionada, o cliente poderá usar todas as formas de pagamento e prazos disponíveis.<br>
                  • Se alguma opção for selecionada, o cliente só poderá usar as opções selecionadas ao criar pedidos.
                </div>
              </v-alert>

              <v-row>
                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="selected_payment_options"
                    :items="all_payment_options"
                    item-text="attributes.display_name"
                    item-value="id"
                    label="Formas de Pagamento Permitidas"
                    multiple
                    chips
                    deletable-chips
                    hint="Deixe vazio para permitir todas"
                    persistent-hint
                    :loading="loading.payment_options"
                  >
                    <template v-slot:no-data>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>
                            Nenhuma forma de pagamento encontrada
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="selected_payment_times"
                    :items="all_payment_times"
                    item-text="attributes.description"
                    item-value="id"
                    label="Prazos de Pagamento Permitidos"
                    multiple
                    chips
                    deletable-chips
                    hint="Deixe vazio para permitir todos"
                    persistent-hint
                    :loading="loading.payment_times"
                  >
                    <template v-slot:no-data>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>
                            Nenhum prazo de pagamento encontrado
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="cancelEdit">Cancelar</v-btn>
              <v-btn color="primary" @click="saveAllowedPayments" :loading="loading.saving">Salvar</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-row>
          <v-col cols="12" md="6">
            <div class="text-subtitle-1 mb-2">
              <v-icon small left>mdi-credit-card</v-icon>
              Formas de Pagamento Permitidas
            </div>
            <div v-if="loading.payment_options" class="d-flex align-center">
              <v-progress-circular indeterminate color="primary" size="20" class="mr-2"></v-progress-circular>
              <span class="grey--text">Carregando formas de pagamento...</span>
            </div>
            <div v-else-if="allowed_payment_options.length === 0" class="text-body-2 grey--text">
              Todas as formas de pagamento estão permitidas
            </div>
            <div v-else>
              <v-chip
                v-for="option in allowed_payment_options"
                :key="option.id"
                small
                class="ma-1"
                color="primary"
              >
                {{ option.attributes.display_name }}
              </v-chip>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="text-subtitle-1 mb-2">
              <v-icon small left>mdi-calendar</v-icon>
              Prazos de Pagamento Permitidos
            </div>
            <div v-if="loading.payment_times" class="d-flex align-center">
              <v-progress-circular indeterminate color="primary" size="20" class="mr-2"></v-progress-circular>
              <span class="grey--text">Carregando prazos de pagamento...</span>
            </div>
            <div v-else-if="allowed_payment_times.length === 0" class="text-body-2 grey--text">
              Todos os prazos de pagamento estão permitidos
            </div>
            <div v-else>
              <v-chip
                v-for="time in allowed_payment_times"
                :key="time.id"
                small
                class="ma-1"
                color="primary"
              >
                {{ time.attributes.description }}
              </v-chip>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Api from '@/api/index';

export default {
  name: 'ClientAllowedPayments',
  data() {
    return {
      editDialog: false,
      selected_payment_options: [],
      selected_payment_times: [],
      all_payment_options: [],
      all_payment_times: [],
      loading: {
        payment_options: false,
        payment_times: false,
        saving: false,
      },
    }
  },
  computed: {
    ...mapState({
      oneClient: state => state.Client.oneClient,
    }),
    client() {
      if (this.oneClient && this.oneClient.data) {
        return this.oneClient;
      }
      return null;
    },
    allowed_payment_option_ids() {
      if (!this.client || !this.client.data || !this.client.data.attributes) return [];
      return this.client.data.attributes.allowed_payment_option_ids || [];
    },
    allowed_payment_time_ids() {
      if (!this.client || !this.client.data || !this.client.data.attributes) return [];
      return this.client.data.attributes.allowed_payment_time_ids || [];
    },
    allowed_payment_options() {
      if (this.allowed_payment_option_ids.length === 0) return [];
      return this.all_payment_options.filter(opt => 
        this.allowed_payment_option_ids.includes(parseInt(opt.id))
      );
    },
    allowed_payment_times() {
      if (this.allowed_payment_time_ids.length === 0) return [];
      return this.all_payment_times.filter(time => 
        this.allowed_payment_time_ids.includes(parseInt(time.id))
      );
    },
    user_allowed() {
      return this.isManager() || this.isDirector() || this.isSalesExecutive() || this.isCreditAnalyst()
    },
  },
  methods: {
    loadPaymentOptions() {
      this.loading.payment_options = true;
      Api.PaymentOption.index('options').then(r => r.data).then(pay => {
        this.all_payment_options = [...pay.data.map(el => ({ ...el, id: parseInt(el.id) }))];
      }).catch(err => {
        console.error(err);
        this.addNotification({ type: 'error', msg: 'Erro ao carregar formas de pagamento' });
      }).finally(() => {
        this.loading.payment_options = false;
      });
    },
    loadPaymentTimes() {
      this.loading.payment_times = true;
      Api.PaymentOption.index('times').then(r => r.data).then(pay => {
        this.all_payment_times = [...pay.data.map(el => ({ ...el, id: parseInt(el.id) }))];
      }).catch(err => {
        console.error(err);
        this.addNotification({ type: 'error', msg: 'Erro ao carregar prazos de pagamento' });
      }).finally(() => {
        this.loading.payment_times = false;
      });
    },
    openEditDialog() {
      this.selected_payment_options = [...(this.allowed_payment_option_ids || [])];
      this.selected_payment_times = [...(this.allowed_payment_time_ids || [])];
      this.editDialog = true;
    },
    cancelEdit() {
      this.editDialog = false;
      this.selected_payment_options = [...(this.allowed_payment_option_ids || [])];
      this.selected_payment_times = [...(this.allowed_payment_time_ids || [])];
    },
    saveAllowedPayments() {
      this.loading.saving = true;
      let id = this.client?.data?.id || this.$route.params.id;
      let client = {
        id,
        allowed_payment_option_ids: this.selected_payment_options,
        allowed_payment_time_ids: this.selected_payment_times,
      };

      
      Api.Client.basicUpdate(client)
        .then(() => {
          this.successUpdated();
          this.editDialog = false;
          this.$emit('updated');
        })
        .catch((error) => {
          console.error(error);
          this.errorUpdated();
        })
        .finally(() => {
          this.loading.saving = false;
        });
    },
  },
  watch: {
    editDialog(newVal) {
      if (newVal) {
        this.openEditDialog();
      }
    },
    oneClient: {
      handler(newVal) {
        // Atualiza quando o client mudar no store
        if (newVal && newVal.data) {
          // Força atualização dos dados
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.loadPaymentOptions();
    this.loadPaymentTimes();
  },
}
</script>

